import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import TextFormInput from "../../Shared/TextInput/TextFormInput";
import { Button, FormWarning } from "@shltelemedicine/shl-ui-lib";
import { Dispatch, SetStateAction, useState } from "react";
import ConnectForm from "../../Shared/ConnectForm/ConnectForm";
import {
  IStudySubject,
  OrderFormDetails,
} from "../../../models/order.interface";
import styles from "./StudySubjectInputs.module.css";
import StudySelectFormInput from "../../Reports/StudySelectFormInput/StudySelectFormInput";
import studiesService from "../../../services/studies.service";
import useAlert from "../../../hooks/useAlert";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import { IStudy } from "../../../models/study.interface";

type StudySubjectInputsProps = {
  updateSubject: Dispatch<SetStateAction<IStudySubject | undefined>>;
  updateStudy: Dispatch<SetStateAction<Partial<IStudy | undefined>>>;
  otherStudyName: string;
  updateOtherStudyName: Dispatch<SetStateAction<string>>;
};

const StudySubjectInputs = ({
  updateSubject,
  updateStudy,
  otherStudyName,
  updateOtherStudyName,
}: StudySubjectInputsProps) => {
  const {
    trigger,
    watch,
    formState: { isSubmitting },
  } = useFormContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [studies, setStudies] = useState<Partial<IStudy>[]>();

  const study = watch("study");
  const subjectId = watch("subjectId");
  const retypeSubjectId = watch("retypeSubjectId");

  const intl = useIntl();

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const subjectIdPlaceholder = intl.messages[
    "ORDERS.SUBJECT_ID.PLACEHOLDER"
  ] as string;

  const retypeSubjectIdPlaceholder = intl.messages[
    "ORDERS.RETYPE_SUBJECT_ID.PLACEHOLDER"
  ] as string;

  const handleShow = async () => {
    // validate
    const isValid = await trigger(["study", "subjectId", "retypeSubjectId"]);

    if (!isValid) return;

    setLoading(true);

    try {
      const {
        success,
        data,
        errorCode,
      } = await studiesService.getOrderByStudySubject(study, subjectId);

      if (!success || !data) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.LOAD_SUBJECT",
          "An error occurred while loading a subject.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      updateOtherStudyName(data.otherStudy?.name ?? "");
      updateStudy(studies ? studies.find((s) => s.id === study) : {});
      updateSubject(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ConnectForm<OrderFormDetails>>
        {({ control, formState: { errors: formErrors } }) => {
          return (
            <Card className="d-flex px-5 pt-4 muted-color app-shadow mx-auto">
              <Form.Row className="flex-column align-items-md-end flex-md-row">
                <Col md={5}>
                  <span className="prim-color">*</span>
                  <span className="mx-sm">
                    <Form.Label className="mb-2 app-form-label">
                      <FormattedMessage
                        id="ORDERS.STUDY"
                        defaultMessage="Study"
                      />
                    </Form.Label>
                  </span>
                  <StudySelectFormInput
                    required={true}
                    control={control}
                    disabled={loading || isSubmitting}
                    isInvalid={!!formErrors?.study}
                    onStudiesChange={setStudies}
                  />
                  {!!formErrors?.study ? (
                    <FormWarning
                      className="font-sm flex-grow-1 mt-2 al"
                      message={
                        <FormattedMessage
                          id="ORDERS.STUDY_REQUIRED"
                          defaultMessage="Select Study"
                        />
                      }
                    />
                  ) : (
                    !!formErrors && (
                      <p
                        className={"text-center font-sm flex-grow-1"}
                        style={{ color: "white", visibility: "hidden" }}
                      >
                        Placeholder
                      </p>
                    )
                  )}
                </Col>
                <Col>
                  <span className="prim-color">*</span>
                  <span className="mx-sm">
                    <Form.Label className="mb-2 app-form-label">
                      <FormattedMessage
                        id="ORDERS.SUBJECT_ID"
                        defaultMessage="Subject ID"
                      />
                    </Form.Label>
                  </span>
                  <TextFormInput
                    name={"subjectId"}
                    control={control}
                    rules={{
                      required: true,
                    }}
                    input={{
                      placeholder: subjectIdPlaceholder,
                      isInvalid: !!formErrors?.subjectId,
                      disabled: loading || isSubmitting,
                    }}
                  />
                  {!!formErrors?.subjectId ? (
                    <FormWarning
                      className="font-sm flex-grow-1 mt-2"
                      message={
                        <FormattedMessage
                          id="ORDERS.SUBJECT_ID_REQUIRED"
                          defaultMessage="Insert Subject ID"
                        />
                      }
                    />
                  ) : (
                    !!formErrors && (
                      <p
                        className={"text-center font-sm flex-grow-1"}
                        style={{ color: "white", visibility: "hidden" }}
                      >
                        Placeholder
                      </p>
                    )
                  )}
                </Col>
                <Col>
                  <span className="prim-color">*</span>
                  <span className="mx-sm">
                    <Form.Label className="mb-2 app-form-label">
                      <FormattedMessage
                        id="ORDERS.RETYPE_SUBJECT_ID"
                        defaultMessage="Retype Subject ID"
                      />
                    </Form.Label>
                  </span>
                  <TextFormInput
                    name={"retypeSubjectId"}
                    control={control}
                    rules={{
                      required: true,
                      validate: (v) => v === subjectId,
                    }}
                    input={{
                      placeholder: retypeSubjectIdPlaceholder,
                      isInvalid:
                        !!formErrors?.retypeSubjectId ||
                        (retypeSubjectId && subjectId !== retypeSubjectId),
                      disabled: loading || isSubmitting,
                    }}
                  />
                  {!!formErrors?.retypeSubjectId ? (
                    <FormWarning
                      className="font-sm flex-grow-1 mt-2"
                      message={
                        <FormattedMessage
                          id="ORDERS.RETYPE_SUBJECT_ID_REQUIRED"
                          defaultMessage="Retype Subject ID"
                        />
                      }
                    />
                  ) : retypeSubjectId && subjectId !== retypeSubjectId ? (
                    <FormWarning
                      className="font-sm flex-grow-1 mt-2"
                      message={
                        <FormattedMessage
                          id="ORDERS.RETYPE_SUBJECT_ID.MISMATCH"
                          defaultMessage="Subject IDs do not match"
                        />
                      }
                    />
                  ) : (
                    !!formErrors && (
                      <p
                        className={"text-center font-sm flex-grow-1"}
                        style={{ color: "white", visibility: "hidden" }}
                      >
                        Placeholder
                      </p>
                    )
                  )}
                </Col>
                <Col xs="auto" className="d-flex flex-column">
                  <Button
                    bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
                    className="align-self-end capitalized"
                    disabled={loading || isSubmitting}
                    onClick={handleShow}
                  >
                    <FormattedMessage id="ORDERS.SHOW" defaultMessage="Show" />
                  </Button>
                  <p
                    className={"text-center font-sm flex-grow-1"}
                    style={{ color: "white", visibility: "hidden" }}
                  >
                    Placeholder
                  </p>
                </Col>
              </Form.Row>
              <div className={`${styles.subjectExistsNote} pb-4`}>
                {!!otherStudyName && (
                  <FormattedMessage
                    id="ORDERS.SUBJECT_ALREADY_EXISTS"
                    defaultMessage="Note: This subject already exists in"
                  />
                )}
                &nbsp;
                <span>{otherStudyName}</span>
              </div>
            </Card>
          );
        }}
      </ConnectForm>
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};

export default StudySubjectInputs;
